import { IApiResponse, IImportants, INote, INoteContent, IScoreboard } from "../../types/INote";
import styles from "./index.module.scss";
import ImportantNotes from "../ImportantNotes";
import { useEffect, useRef, useState } from "react";
import getData from "../../services/getData";
import prepareRequest from "../../services/prepare-request";
import ButtonMore from "../ButtonMore";
import NoteContent from "../NoteContent";
import NewNoteButton from "../NewNoteButton";
import Loader from "../Loader";
import Ad from "../Ad";
import { buildImportantNote, clearFirstShow } from "../../services/importantNote";
import { updateNotes, prepareData, byte, WSFrame, findNote, deleteNote } from "../../services/websockets";
import { useInView } from "react-intersection-observer";
import urlStore from "../../services/urlStore";
import logger from "../../services/logger";
import Scoreboard from "../Scoreboard";
import StatusLabel from "../StatusLabel";
import ButtonSort from "../ButtonSort";
import NoteHead from "../NoteHead";
import NoteAuthor from "../NoteAuthor";
import NoteTitle from "../NoteTitle";
import StructuredData from "../StructuredData";
import { usePageData } from "../../hooks/usePageData";
import { useTestMode } from "../../hooks/useTestMode";
import { useLiveType } from "../../hooks/useLiveType";
import { useAdblockCheck } from "../../hooks/useAdblockCheck";

export default function Rtc(): JSX.Element {
  const { articleId } = usePageData();
  const { testArticleId } = useTestMode();
  const { liveType } = useLiveType();

  const xx = testArticleId || articleId;

  if (!xx) {
    logger.error("RTC: no xx found!");
    return <></>;
  }

  const isMobile = window.gazeta_pl?.device !== "NOT_MOBILE";
  const { ref: topRTC, inView } = useInView({
    threshold: 0,
    rootMargin: "100px",
  });
  const [notes, setNotes] = useState<Array<INote>>([]);
  const [importants, setImportants] = useState<Array<IImportants>>([]);
  const [oldest, setOldest] = useState(false);
  const [endOfNotes, setEndOfNotes] = useState(false);
  const [loadMore, setLoadMore] = useState(false);
  const [clickedNoteId, setClickedNoteId] = useState<string | undefined>(undefined);
  const limitPerPage = 20;
  const [isRtcLive, setIsRtcLive] = useState(false);
  const [numberOfNewNotes, setNumberOfNewNotes] = useState(0);
  const [newNotes, setNewNotes] = useState<Array<INote>>([]);
  const notesRef = useRef<HTMLDivElement | null>(null);
  const [msgFromWS, setMsgFromWS] = useState<INote | undefined>();
  const [msgFromWSForScoreboard, setMsgFromWSForScoreboard] = useState<IScoreboard | undefined>();
  const [topRtcInViewPort, setTopRtcInViewPort] = useState(false);
  const [loadData, setLoadData] = useState(false);
  let adsIndex = 0;

  const getIsLive = () => {
    /** set TRUE if Rtc is live */
    setIsRtcLive(window.gazeta_pl?.rtcIsLive || false);
  };

  useEffect(() => {
    setTopRtcInViewPort(inView);
    if (inView && newNotes.length) {
      addNewNotes();
    }
  }, [inView]);

  useEffect(() => {
    getNotes("sort", notes && !notes.length);
  }, [oldest]);

  useEffect(() => {
    if (loadMore) {
      getMoreNotes();
    }

    async function getMoreNotes() {
      /** check if note exists */
      const existingNote: INote | undefined = notes.find((note: INote) => note.id === clickedNoteId);

      if (!existingNote && !endOfNotes) {
        await getNotes("more");
        setLoadMore(false);

        /** if clicked note not exists - load notes until the clicked note is loaded */
        setTimeout(async () => {
          setLoadMore(true);
        }, 10);
      } else {
        /** if clicked note exists - scroll to note */
        if (notesRef && notesRef.current) {
          setLoadMore(false);
          notesRef.current.querySelector(`#note-${clickedNoteId}`)?.scrollIntoView(true);
        }
      }
    }
  }, [loadMore]);

  useEffect(() => {
    getIsLive();
    /**
     * websockets
     */
    const ws = new WebSocket(urlStore.urlWS);

    const wsOpen = () => {
      const connectFrame = WSFrame("CONNECT", { "accept-version": "1.1,1.0", "heart-beat": "10000,10000" }, byte);
      ws.send(connectFrame.toString());

      const subscribeFrame = WSFrame("SUBSCRIBE", { id: "sub-0", destination: "/topic/ws-live-gazeta/" + xx }, byte);
      ws.send(subscribeFrame.toString());

      const subscribeFrameSB = WSFrame("SUBSCRIBE", { id: "sub-1", destination: "/topic/ws-live/" + xx }, byte);
      ws.send(subscribeFrameSB.toString());
    };

    const wsError = () => {
      logger.error("RTC: error web socket");
    };

    const wsMessage = (event: MessageEvent) => {
      const noteData = prepareData(byte, event.data);

      if (noteData) {
        if (liveType === "SPORT" && noteData.matchHeaders) {
          setMsgFromWSForScoreboard(noteData);
        } else if (!noteData.htmlDesktop) {
          setMsgFromWS(noteData);
        }
      }
    };

    ws.addEventListener("open", wsOpen);
    ws.addEventListener("error", wsError);
    ws.addEventListener("message", wsMessage);

    return () => {
      ws.removeEventListener("open", wsOpen);
      ws.removeEventListener("error", wsError);
      ws.removeEventListener("message", wsMessage);
      ws.close();
    };
  }, []);

  useEffect(() => {
    if (msgFromWS) {
      getIsLive();

      if (msgFromWS.status === "EDIT") {
        const oldNote = findNote(notes, msgFromWS);
        const newNote = { ...oldNote, ...msgFromWS };
        const itWasImportant = oldNote?.important;

        if (oldNote && newNote) {
          setNotes((prev) => updateNotes(prev, newNote));
        }

        if (itWasImportant) {
          if (newNote.important) {
            const oldImportantNote: IImportants | undefined = findNote(importants, msgFromWS);
            const newImportantNote: IImportants | undefined = buildImportantNote(msgFromWS);

            if (oldImportantNote && newImportantNote) {
              const newImportantData: IImportants | undefined = { ...oldImportantNote, ...newImportantNote };

              setImportants((prev) => updateNotes(prev, newImportantData));
            }
          } else {
            setImportants(deleteNote(importants, oldNote.id));
          }
        } else {
          setImportants((prev) => [newNote, ...prev]);
        }
      } else if (msgFromWS.status === "DELETE") {
        setNotes((prev) => deleteNote(prev, msgFromWS.id));
        setImportants((prev) => deleteNote(prev, msgFromWS.id));
      } else {
        setNewNotes((prev) => [...prev, msgFromWS]);
      }
    }
  }, [msgFromWS]);

  useEffect(() => {
    if (!oldest && newNotes.length) {
      if (topRtcInViewPort) {
        addNewNotes(); //add new notes
      } else {
        setNumberOfNewNotes((prev) => prev + 1); //show Popup
      }
    }
  }, [newNotes]);

  const getNotes = async (mode?: string, first?: boolean): Promise<void> => {
    setLoadData(true);

    let response: IApiResponse = {
      timeline: [],
      notes: [],
    };
    const request = prepareRequest(notes, notes.length, oldest, mode, first);
    response = await getData(xx, request, limitPerPage);

    switch (mode) {
      /**
       * user was clicked MORE button (read next notes)
       * or user was clicked item from list of importants notes
       */
      case "more":
        setNotes((notes) => [...notes, ...response?.notes]);
        break;

      /**
       * user was clicked SORT button (notes in ascending order)
       */
      default:
        setNotes(response?.notes);
        setEndOfNotes(false);
        break;
    }

    /** set list of important notes */
    if (response?.timeline.length) {
      setImportants(response.timeline);
    }

    /** set flag to true when end of notes */
    if (response?.notes.length < limitPerPage) {
      setEndOfNotes(true);
    }

    setLoadData(false);
  };

  const addNewNotes = () => {
    if (newNotes.length) {
      newNotes.forEach((note: INote) => {
        if (note.important) {
          const importantNote: IImportants | undefined = buildImportantNote(note);

          if (importantNote) {
            setImportants((prev) => [importantNote, ...prev]);
          }
        }
        const newNote = { ...note, firstShow: true };

        if (!oldest) {
          setNotes((prev) => [newNote, ...prev]);
        } else {
          setNotes((prev) => [...prev, newNote]);
        }
      });

      setNewNotes([]);
      setNumberOfNewNotes(0);
      setTimeout(() => {
        setNotes((prev) => clearFirstShow(prev));
      }, 3000);
    }
  };

  const { isAdblockDetected } = useAdblockCheck();

  return (
    <>
      <NewNoteButton numberOfNewNotes={numberOfNewNotes} addNewNote={addNewNotes} />
      {liveType === "SPORT" && (
        <Scoreboard scoreboardWS={msgFromWSForScoreboard} xx={xx} isRtcLive={isRtcLive} isMobile={isMobile} />
      )}
      {importants && importants.length > 0 && (
        <ImportantNotes
          setLoadMore={setLoadMore}
          setClickedNoteId={setClickedNoteId}
          importants={importants}
          isMobile={isMobile}
        />
      )}

      <div className={[styles.notes, isMobile ? styles.mobile : ""].join(" ")} data-e="notes" ref={notesRef}>
        <div className={styles.notes__top}>
          <div className={styles.notes__title} id="topRTC" ref={topRTC}>
            Relacja na żywo
          </div>
          <StatusLabel isRtcLive={isRtcLive} />
          <ButtonSort setOldest={setOldest} oldest={oldest} />
        </div>

        {notes?.map((note: INote, i: number) => {
          const adsStep = 3;
          let showAd = (i + 1) % adsStep === 0;

          if (showAd) {
            adsIndex++;
          }
          if (endOfNotes && i === notes.length - 1) {
            showAd = true;
            adsIndex = 100;
          }

          return (
            <div
              key={`note-${note.id}-${i}`}
              className={[
                styles.noteWrapper,
                showAd && isMobile ? styles.containAd : "",
                isMobile ? styles.noteWrapper__mobile : "",
              ].join(" ")}
            >
              <div
                className={[
                  styles.note,
                  note.urgent ? styles.note_urgent : "",
                  note.firstShow ? styles.note_firstShow : "",
                  i === 0 ? styles.firstNote : "",
                ].join(" ")}
                id={`note-${note.id}`}
                data-e="note"
              >
                <NoteHead note={note} />
                {!!note.noteAuthors.length && <NoteAuthor authors={note.noteAuthors} />}
                {note.title && <NoteTitle title={note.title} />}
                <div
                  className={[styles.note__content, isMobile ? styles.note__content_mobile : ""].join(" ")}
                  data-e="note-content"
                >
                  {note.content &&
                    note.content.map((content: INoteContent, idx: number) => (
                      <NoteContent
                        noteId={note.id}
                        content={content}
                        embedArticles={note.embedArticles || undefined}
                        isMobile={isMobile}
                        key={`note-cnt-${note.id}-${idx}`}
                      />
                    ))}
                </div>
              </div>
              {showAd && !isAdblockDetected && <Ad adsIndex={adsIndex} isMobile={isMobile} />}
            </div>
          );
        })}

        {!endOfNotes && loadData && <Loader />}
      </div>

      {!endOfNotes && <ButtonMore isMobile={isMobile} getNotes={getNotes} />}
      <StructuredData articleId={xx} liveType={liveType} notes={notes} />
    </>
  );
}
